import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useStoreSetup } from '../../hooks/useStoreSetup';
import Loading from '../../components/common/Loading';
import Modal from '../../components/common/Modal';
import { 
  LuStore,
  LuChevronLeft,
  LuCamera,
  LuPackage,
  LuWrench,
  LuBuilding,
  LuCheck,
  LuX,
  LuPlus,
  LuClock,
  LuMapPin,
  LuPhone,
  LuCircle,
  LuWind,
  LuCar,
  LuSettings,
  LuUpload,
  LuChevronDown
} from 'react-icons/lu';

export default function SetupStoreScreen() {
  const navigate = useNavigate();
  const { user, updateUserProfile } = useAuth();
  const { setupStore, handleUploadStoreImage, loading, uploadLoading, error } = useStoreSetup();

  // Form state
  const [formData, setFormData] = useState({
    user_id: user?.user_id,
    store_name: '',
    store_description: '',
    store_location: '',
    phone_number: user?.phone_number || '',
    store_type: 'products',
    delivery_options: 'both',
    delivery_fee_min: '',
    delivery_fee_max: '',
    delivery_coverage_areas: [],
    opening_time: '09:00',
    closing_time: '17:00',
    is_24_hours: false,
  });

  // Image states
  const [storeLogo, setStoreLogo] = useState(null);
  const [storeLogoFile, setStoreLogoFile] = useState(null);
  const [storeCoverImage, setStoreCoverImage] = useState(null);
  const [storeCoverFile, setStoreCoverFile] = useState(null);
  const [imagePreview, setImagePreview] = useState({
    logo: null,
    cover: null
  });

  // UI states
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [showStoreTypeModal, setShowStoreTypeModal] = useState(false);
  const [coverageAreaInput, setCoverageAreaInput] = useState('');
  const [imageUploading, setImageUploading] = useState(null);

  /* ------------------------------------------------------------------ */
  /*  STORE TYPES                                                      */
  /* ------------------------------------------------------------------ */
  const storeTypes = [
    { 
      value: 'products', 
      label: 'Selling Products', 
      icon: LuPackage,
      description: 'You sell physical items like electronics, fashion, books, etc.'
    },
    { 
      value: 'services', 
      label: 'Offering Services', 
      icon: LuWrench,
      description: 'You offer services like tutoring, repairs, photography, etc.'
    },
    { 
      value: 'both', 
      label: 'Both Products & Services', 
      icon: LuBuilding,
      description: 'You sell both physical items and offer services'
    }
  ];

  /* ------------------------------------------------------------------ */
  /*  DELIVERY OPTIONS                                                 */
  /* ------------------------------------------------------------------ */
  const deliveryOptions = [
    { value: 'pickup', label: 'Pickup Only', icon: LuWind },
    { value: 'delivery', label: 'Delivery Only', icon: LuCar },
    { value: 'both', label: 'Both Pickup & Delivery', icon: LuSettings },
  ];

  /* ------------------------------------------------------------------ */
  /*  FORM HANDLERS                                                    */
  /* ------------------------------------------------------------------ */
  const updateForm = (updates) => {
    setFormData(prev => ({ ...prev, ...updates }));
  };

  const handle24HoursToggle = () => {
    const new24Hours = !formData.is_24_hours;
    setFormData(prev => ({ 
      ...prev,
      is_24_hours: new24Hours,
      // When 24hrs is true, set times to null (not empty string)
      opening_time: new24Hours ? null : '09:00',
      closing_time: new24Hours ? null : '17:00'
    }));
  };

  const handleStoreTypeSelect = (type) => {
    updateForm({ store_type: type });
    setShowStoreTypeModal(false);
    
    if (type === 'services') {
      updateForm({ 
        delivery_options: 'both',
        delivery_fee_min: '',
        delivery_fee_max: ''
      });
    }
  };

  const handleDeliveryOptionSelect = (option) => {
    updateForm({ delivery_options: option });
    setShowDeliveryModal(false);
    
    if (option === 'pickup') {
      updateForm({ delivery_fee_min: '', delivery_fee_max: '' });
    }
  };

  /* ------------------------------------------------------------------ */
  /*  IMAGE HANDLERS                                                   */
  /* ------------------------------------------------------------------ */
  const handleImageUpload = async (file, imageType) => {
    if (!file) return;
    
    setImageUploading(imageType);
    
    try {
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(prev => ({
          ...prev,
          [imageType]: reader.result
        }));
      };
      reader.readAsDataURL(file);

      // Store the file for later upload
      if (imageType === 'logo') {
        setStoreLogoFile(file);
      } else if (imageType === 'cover') {
        setStoreCoverFile(file);
      }
      
      setImageUploading(null);
      
    } catch (error) {
      // console.error('Image upload error:', error);
      setImageUploading(null);
    }
  };

  const uploadImages = async () => {
    const uploadedImages = {};
    
    // Upload logo if exists
    if (storeLogoFile) {
      try {
        const result = await handleUploadStoreImage(user.user_id, storeLogoFile, 'logo');
        if (result?.data?.image_url) {
          uploadedImages.store_logo_url = result.data.image_url;
          setStoreLogo(result.data.image_url);
        }
      } catch (error) {
        // console.error('Logo upload failed:', error);
        throw new Error('Failed to upload logo image');
      }
    }
    
    // Upload cover if exists
    if (storeCoverFile) {
      try {
        const result = await handleUploadStoreImage(user.user_id, storeCoverFile, 'cover');
        if (result?.data?.image_url) {
          uploadedImages.store_cover_picture_url = result.data.image_url;
          setStoreCoverImage(result.data.image_url);
        }
      } catch (error) {
        // console.error('Cover image upload failed:', error);
        throw new Error('Failed to upload cover image');
      }
    }
    
    return uploadedImages;
  };

  const renderImageUpload = (title, imageType, description) => (
    <div className="mb-6">
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {title}
      </label>
      <p className="text-sm text-gray-500 mb-3">{description}</p>
      
      <div className="mt-1">
        <div className="flex items-center justify-center w-full">
          <label className={`
            relative flex flex-col items-center justify-center w-full h-40 border-2 border-dashed rounded-lg cursor-pointer
            ${imagePreview[imageType] ? 'border-orange-300 bg-orange-50' : 'border-gray-300 hover:border-gray-400 bg-gray-50'}
            ${imageUploading === imageType ? 'opacity-50 cursor-not-allowed' : ''}
          `}>
            {imagePreview[imageType] ? (
              <div className="relative w-full h-full">
                <img 
                  src={imagePreview[imageType]} 
                  alt={title}
                  className="w-full h-full object-cover rounded-lg"
                />
                <div className="absolute inset-0 bg-black bg-opacity-40 rounded-lg flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                  <LuCamera className="w-8 h-8 text-white" />
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center pt-5 pb-6">
                <LuUpload className="w-10 h-10 text-gray-400 mb-2" />
                <p className="text-sm text-gray-500 mb-1">
                  <span className="font-semibold">Click to upload</span> or drag and drop
                </p>
                <p className="text-xs text-gray-500">
                  PNG, JPG up to 5MB
                </p>
              </div>
            )}
            <input 
              type="file" 
              className="hidden" 
              accept="image/*"
              onChange={(e) => handleImageUpload(e.target.files[0], imageType)}
              disabled={imageUploading === imageType || loading}
            />
          </label>
        </div>
        
        {imageUploading === imageType && (
          <div className="mt-2 flex items-center text-sm text-gray-600">
            <Loading size="small" />
            <span className="ml-2">Uploading...</span>
          </div>
        )}
      </div>
    </div>
  );

  /* ------------------------------------------------------------------ */
  /*  COVERAGE AREAS                                                   */
  /* ------------------------------------------------------------------ */
  const addCoverageArea = () => {
    if (coverageAreaInput.trim() && !formData.delivery_coverage_areas.includes(coverageAreaInput.trim())) {
      updateForm({
        delivery_coverage_areas: [...formData.delivery_coverage_areas, coverageAreaInput.trim()]
      });
      setCoverageAreaInput('');
    }
  };

  const removeCoverageArea = (area) => {
    updateForm({
      delivery_coverage_areas: formData.delivery_coverage_areas.filter(a => a !== area)
    });
  };

  const renderCoverageArea = (area, index) => (
    <div key={index} className="inline-flex items-center gap-2 bg-gray-100 text-gray-800 px-3 py-1.5 rounded-full text-sm">
      {area}
      <button
        type="button"
        onClick={() => removeCoverageArea(area)}
        className="text-gray-500 hover:text-gray-700"
        disabled={loading}
      >
        <LuX className="w-3 h-3" />
      </button>
    </div>
  );

  /* ------------------------------------------------------------------ */
  /*  VALIDATION & SUBMISSION                                          */
  /* ------------------------------------------------------------------ */
  const validateForm = () => {
    const required = ['store_name', 'store_location', 'phone_number'];
    
    for (let field of required) {
      if (!formData[field]?.trim()) {
        alert(`Please fill in ${field.replace('_', ' ')}`);
        return false;
      }
    }

    if (formData.phone_number && !/^\d{11}$/.test(formData.phone_number)) {
      alert('Please enter a valid 11-digit phone number');
      return false;
    }

    if (formData.delivery_options !== 'pickup') {
      if (!formData.delivery_fee_min || !formData.delivery_fee_max) {
        alert('Please set delivery fee range');
        return false;
      }
      
      const min = parseFloat(formData.delivery_fee_min);
      const max = parseFloat(formData.delivery_fee_max);
      
      if (min >= max) {
        alert('Minimum fee must be less than maximum fee');
        return false;
      }
      
      if (min < 0 || max < 0) {
        alert('Delivery fees cannot be negative');
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      // Calculate average delivery fee for backend
      const deliveryFee = formData.delivery_options !== 'pickup' 
        ? (parseFloat(formData.delivery_fee_min) + parseFloat(formData.delivery_fee_max)) / 2
        : 0;

      // Prepare base data
      const submitData = {
        ...formData,
        user_id: user.user_id,
        delivery_fee: deliveryFee,
        phone_number: formData.phone_number.replace(/[^0-9]/g, '')
      };

      // Upload images first if they exist
      let uploadedImages = {};
      if (storeLogoFile || storeCoverFile) {
        uploadedImages = await uploadImages();
      }

      // Combine form data with uploaded image URLs
      const finalData = {
        ...submitData,
        ...uploadedImages
      };

      // Submit store setup
      const result = await setupStore(finalData);
      
      if (result.success) {
        // Update user context to mark as seller
        updateUserProfile({ 
          ...user, 
          is_seller: true,
          store_id: result.data?.store_id,
          store_name: formData.store_name
        });
        
        // Show success message
        if (window.confirm(
          `🎉 Your ${formData.store_type === 'services' ? 'service' : 'store'} has been created successfully!\n\nYou can now start ${formData.store_type === 'services' ? 'offering services' : 'selling'}!`
        )) {
          navigate('/store/dashboard');
        }
      }
    } catch (err) {
      // console.error('Store setup error:', err);
      alert(err.message || 'Failed to setup store. Please try again.');
    }
  };

  /* ------------------------------------------------------------------ */
  /*  RENDER                                                           */
  /* ------------------------------------------------------------------ */
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loading size="large" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-white border-b border-gray-200 px-4 lg:px-8 py-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            disabled={loading}
          >
            <LuChevronLeft className="w-5 h-5 text-gray-600" />
          </button>
          <h1 className="text-lg lg:text-xl font-bold text-gray-900">
            Setup Your Store
          </h1>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 lg:px-0 py-6">
        <div className="space-y-6">
          {/* Header Section */}
          <div className="bg-white rounded-xl p-6 lg:p-8 shadow-sm">
            <div className="flex flex-col items-center text-center mb-6">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                <LuStore className="w-8 h-8 text-orange-600" />
              </div>
              <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-2">
                Setup Your Store
              </h2>
              <p className="text-gray-600">
                Complete your store profile to start {formData.store_type === 'services' ? 'offering services' : 'selling'}
              </p>
            </div>
          </div>

          {/* Store Type */}
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              What are you offering? *
            </h3>
            
            <button
              onClick={() => setShowStoreTypeModal(true)}
              className="w-full flex items-center justify-between p-4 border border-gray-300 rounded-lg hover:border-gray-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              <div className="flex items-center gap-3">
                {(() => {
                  const Icon = storeTypes.find(t => t.value === formData.store_type)?.icon || LuPackage;
                  return <Icon className="w-5 h-5 text-orange-600" />;
                })()}
                <span className="font-medium text-gray-900">
                  {storeTypes.find(t => t.value === formData.store_type)?.label || 'Select what you offer'}
                </span>
              </div>
              <LuChevronDown className="w-5 h-5 text-gray-400" />
            </button>

            {formData.store_type && (
              <p className="mt-2 text-sm text-green-600">
                {storeTypes.find(t => t.value === formData.store_type)?.description}
              </p>
            )}
          </div>

          {/* Store Information */}
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">
              {formData.store_type === 'services' ? 'Service Information' : 'Store Information'}
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {formData.store_type === 'services' ? 'Service Name *' : 'Store Name *'}
                </label>
                <input
                  type="text"
                  value={formData.store_name}
                  onChange={(e) => updateForm({ store_name: e.target.value })}
                  placeholder={formData.store_type === 'services' ? 'Enter your service name' : 'Enter your store name'}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={loading}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {formData.store_type === 'services' ? 'Service Description' : 'Store Description'}
                </label>
                <textarea
                  value={formData.store_description}
                  onChange={(e) => updateForm({ store_description: e.target.value })}
                  placeholder={formData.store_type === 'services' ? 'Describe your services...' : 'Describe what you sell...'}
                  rows="3"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition resize-none disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={loading}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contact Phone Number *
                </label>
                <input
                  type="tel"
                  value={formData.phone_number}
                  onChange={(e) => updateForm({ phone_number: e.target.value.replace(/[^0-9]/g, '') })}
                  placeholder="08012345678"
                  maxLength="11"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={loading}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {formData.store_type === 'services' ? 'Service Location' : 'Store Location'} *
                </label>
                <div className="relative">
                  <LuMapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={formData.store_location}
                    onChange={(e) => updateForm({ store_location: e.target.value })}
                    placeholder={formData.store_type === 'services' ? 'Where do you provide services?' : 'Full store address'}
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={loading}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Business Hours */}
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">
              Business Hours
            </h3>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-700">Open 24/7</span>
                <button
                  onClick={handle24HoursToggle}
                  className={`
                    relative inline-flex h-6 w-11 items-center rounded-full transition-colors
                    ${formData.is_24_hours ? 'bg-orange-600' : 'bg-gray-200'}
                    disabled:opacity-50 disabled:cursor-not-allowed
                  `}
                  disabled={loading}
                >
                  <span className={`
                    inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                    ${formData.is_24_hours ? 'translate-x-6' : 'translate-x-1'}
                  `} />
                </button>
              </div>

              {!formData.is_24_hours && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Opening Time
                    </label>
                    <input
                      type="time"
                      value={formData.opening_time}
                      onChange={(e) => updateForm({ opening_time: e.target.value })}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={loading}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Closing Time
                    </label>
                    <input
                      type="time"
                      value={formData.closing_time}
                      onChange={(e) => updateForm({ closing_time: e.target.value })}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={loading}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Delivery Options */}
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">
              {formData.store_type === 'services' ? 'Service Delivery Options' : 'Delivery Options'}
            </h3>
            
            <div className="space-y-6">
              <button
                onClick={() => setShowDeliveryModal(true)}
                className="w-full flex items-center justify-between p-4 border border-gray-300 rounded-lg hover:border-gray-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={loading}
              >
                <span className="font-medium text-gray-900">
                  {deliveryOptions.find(d => d.value === formData.delivery_options)?.label || 'Select delivery option'}
                </span>
                <LuChevronDown className="w-5 h-5 text-gray-400" />
              </button>

              {/* Delivery Coverage Areas */}
              {(formData.delivery_options === 'delivery' || formData.delivery_options === 'both') && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">
                    {formData.store_type === 'services' ? 'Service Coverage Areas' : 'Delivery Coverage Areas'} *
                  </h4>
                  <p className="text-sm text-gray-500 mb-3">
                    {formData.store_type === 'services'
                      ? 'Add areas where you provide services'
                      : 'Add areas where you deliver to'
                    }
                  </p>
                  
                  <div className="flex gap-2 mb-3">
                    <input
                      type="text"
                      value={coverageAreaInput}
                      onChange={(e) => setCoverageAreaInput(e.target.value)}
                      placeholder={formData.store_type === 'services' 
                        ? "Add service area (e.g., Ikeja, Victoria Island)..." 
                        : "Add delivery area..."
                      }
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition disabled:opacity-50 disabled:cursor-not-allowed"
                      onKeyPress={(e) => e.key === 'Enter' && addCoverageArea()}
                      disabled={loading}
                    />
                    <button
                      onClick={addCoverageArea}
                      disabled={!coverageAreaInput.trim() || loading}
                      className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      <LuPlus className="w-4 h-4" />
                      Add
                    </button>
                  </div>
                  
                  {formData.delivery_coverage_areas.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {formData.delivery_coverage_areas.map(renderCoverageArea)}
                    </div>
                  )}
                </div>
              )}

              {/* Delivery Fee Range */}
              {formData.delivery_options !== 'pickup' && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">
                    {formData.store_type === 'services' ? 'Service Fee Range' : 'Delivery Fee Range'} *
                  </h4>
                  <p className="text-sm text-gray-500 mb-3">
                    {formData.store_type === 'services' 
                      ? 'Set your service fee range (e.g., ₦2,500 - ₦5,000)'
                      : 'Set your delivery fee range (e.g., ₦500 - ₦2,000)'}
                  </p>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Minimum (₦)
                      </label>
                      <input
                        type="number"
                        value={formData.delivery_fee_min}
                        onChange={(e) => updateForm({ delivery_fee_min: e.target.value })}
                        placeholder="0"
                        min="0"
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={loading}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Maximum (₦)
                      </label>
                      <input
                        type="number"
                        value={formData.delivery_fee_max}
                        onChange={(e) => updateForm({ delivery_fee_max: e.target.value })}
                        placeholder="0"
                        min="0"
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={loading}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Store Images */}
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">
              Store Images
            </h3>
            
            <div className="space-y-8">
              {renderImageUpload(
                'Store Logo',
                'logo',
                'Recommended: 500x500px, PNG or JPG'
              )}
              
              {renderImageUpload(
                'Store Cover Image',
                'cover',
                'Recommended: 1200x600px, banner image for your store'
              )}
            </div>
          </div>

          {/* Submit Button */}
          <div className="sticky bottom-6 bg-white rounded-xl p-6 shadow-lg border border-gray-200">
            {error && (
              <div className="flex items-center gap-2 text-red-600 bg-red-50 p-3 rounded-lg mb-4">
                <LuCircle className="w-5 h-5" />
                <span className="text-sm font-medium">{error}</span>
              </div>
            )}
            
            <button
              onClick={handleSubmit}
              disabled={loading || uploadLoading}
              className="w-full bg-orange-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-orange-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loading size="small" color="white" />
                  Setting Up...
                </>
              ) : (
                <>
                  Complete {formData.store_type === 'services' ? 'Service' : 'Store'} Setup
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Store Type Modal */}
      <Modal
        isOpen={showStoreTypeModal}
        onClose={() => setShowStoreTypeModal(false)}
        title="What are you offering?"
      >
        <div className="space-y-3">
          {storeTypes.map((type) => {
            const Icon = type.icon;
            return (
              <button
                key={type.value}
                onClick={() => handleStoreTypeSelect(type.value)}
                className={`
                  w-full text-left p-4 rounded-lg border transition-colors
                  ${formData.store_type === type.value 
                    ? 'border-orange-300 bg-orange-50' 
                    : 'border-gray-200 hover:bg-gray-50'
                  }
                `}
              >
                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-lg ${formData.store_type === type.value ? 'bg-orange-100' : 'bg-gray-100'}`}>
                    <Icon className={`w-6 h-6 ${formData.store_type === type.value ? 'text-orange-600' : 'text-gray-600'}`} />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900 mb-1">
                      {type.label}
                    </h4>
                    <p className="text-sm text-gray-600">
                      {type.description}
                    </p>
                  </div>
                  {formData.store_type === type.value && (
                    <LuCheck className="w-5 h-5 text-orange-600" />
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </Modal>

      {/* Delivery Options Modal */}
      <Modal
        isOpen={showDeliveryModal}
        onClose={() => setShowDeliveryModal(false)}
        title={formData.store_type === 'services' ? 'Service Delivery' : 'Delivery Options'}
      >
        <div className="space-y-3">
          {deliveryOptions.map((option) => {
            const Icon = option.icon;
            return (
              <button
                key={option.value}
                onClick={() => handleDeliveryOptionSelect(option.value)}
                className={`
                  w-full flex items-center justify-between p-4 rounded-lg border transition-colors
                  ${formData.delivery_options === option.value 
                    ? 'border-orange-300 bg-orange-50' 
                    : 'border-gray-200 hover:bg-gray-50'
                  }
                `}
              >
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${formData.delivery_options === option.value ? 'bg-orange-100' : 'bg-gray-100'}`}>
                    <Icon className={`w-5 h-5 ${formData.delivery_options === option.value ? 'text-orange-600' : 'text-gray-600'}`} />
                  </div>
                  <span className="font-medium text-gray-900">
                    {option.label}
                  </span>
                </div>
                {formData.delivery_options === option.value && (
                  <LuCheck className="w-5 h-5 text-orange-600" />
                )}
              </button>
            );
          })}
        </div>
      </Modal>
    </div>
  );
}